import { SiteHeader } from "@/components/site-header"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import { PERMISSIONS, type AppResource, type AppRole } from "@cdc-sccr/shared"
import { MinusIcon, EyeIcon, PencilIcon, Trash2Icon, RotateCcwIcon } from "lucide-react"
import { cn } from "@/lib/utils"

// ─── Types ────────────────────────────────────────────────────────────────────

type NavItem  = { label: string; resource: AppResource }
type NavGroup = { group: string; items: NavItem[] }

// ─── Nav structure — mirrors app-sidebar.tsx exactly ─────────────────────────

const NAV_GROUPS: NavGroup[] = [
  {
    group: "Geografía",
    items: [
      { label: "Países",                  resource: "geo" },
      { label: "Provincias",              resource: "geo" },
      { label: "Ciudades y Trayectorias", resource: "geo" },
      { label: "Rutas",                   resource: "geo" },
    ],
  },
  {
    group: "Viajes",
    items: [
      { label: "Viajes",            resource: "trips" },
      { label: "Tipos de servicio", resource: "trips" },
    ],
  },
  {
    group: "Cargas",
    items: [
      { label: "Todas",             resource: "shipments" },
      { label: "Nacional",          resource: "cargo" },
      { label: "Internacional",     resource: "cargo" },
      { label: "Batea",             resource: "cargo" },
      { label: "Productos",         resource: "cargo" },
      { label: "Tipos de carga",    resource: "cargo" },
    ],
  },
  {
    group: "SmartGT",
    items: [
      { label: "CRTs",                  resource: "smartgt" },
      { label: "Chofer",                resource: "smartgt" },
      { label: "Log de importación CRT", resource: "audit" },
    ],
  },
  {
    group: "Flota",
    items: [
      { label: "Tipos de unidad", resource: "units" },
      { label: "Unidades",        resource: "units" },
      { label: "Móviles",         resource: "mobiles" },
    ],
  },
  {
    group: "Relaciones",
    items: [
      { label: "Partes",   resource: "parties" },
      { label: "Clientes", resource: "clients" },
      { label: "Choferes", resource: "drivers" },
      { label: "Remitos",  resource: "drivers" },
    ],
  },
  {
    group: "Sistema",
    items: [
      { label: "Usuarios",           resource: "users"   },
      { label: "Roles y permisos",   resource: "roles"   },
      { label: "Auditoría",          resource: "audit"   },
      { label: "Tipos de documento", resource: "config"  },
      { label: "Docs. de viaje",     resource: "shipmentDocTypes" },
      { label: "Monedas",            resource: "config"  },
      { label: "Conceptos de pago",  resource: "config"  },
    ],
  },
  {
    group: "Ayuda",
    items: [
      { label: "Documentación", resource: "docs"    },
      { label: "Reportes",      resource: "reports" },
    ],
  },
]

// ─── Roles ────────────────────────────────────────────────────────────────────

const ROLES: AppRole[] = ["admin", "operator", "fleet_manager", "driver"]

const ROLE_LABELS: Record<AppRole, string> = {
  admin:         "Admin",
  operator:      "Operador",
  fleet_manager: "Fleet Manager",
  driver:        "Chofer",
}

const ROLE_CLASS: Record<AppRole, string> = {
  admin:         "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
  operator:      "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  fleet_manager: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  driver:        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ActionPip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span
      title={label}
      className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground whitespace-nowrap"
    >
      {icon}
      <span>{label}</span>
    </span>
  )
}

function PermCell({ role, resource }: { role: AppRole; resource: AppResource }) {
  const p = PERMISSIONS[role][resource]
  const hasAny = p.visit || p.write || p.delete || p.restore

  if (!hasAny) {
    return (
      <TableCell className="text-center py-2">
        <MinusIcon className="h-3.5 w-3.5 mx-auto text-muted-foreground/30" />
      </TableCell>
    )
  }

  return (
    <TableCell className="py-2">
      <div className="flex items-center justify-center gap-1 flex-wrap">
        {p.visit   && <ActionPip icon={<EyeIcon       className="h-3 w-3" />} label="Ver"       />}
        {p.write   && <ActionPip icon={<PencilIcon     className="h-3 w-3" />} label="Editar"    />}
        {p.delete  && <ActionPip icon={<Trash2Icon     className="h-3 w-3" />} label="Eliminar"  />}
        {p.restore && <ActionPip icon={<RotateCcwIcon  className="h-3 w-3" />} label="Restaurar" />}
      </div>
    </TableCell>
  )
}

// ─── Role summary cards ───────────────────────────────────────────────────────

function RoleCard({ role }: { role: AppRole }) {
  const allResources = NAV_GROUPS.flatMap(g => g.items.map(i => i.resource))
  const unique = [...new Set(allResources)]
  const perms  = PERMISSIONS[role]
  const visible = unique.filter(r => perms[r].visit).length
  const writable = unique.filter(r => perms[r].write).length
  const hasAccess = visible > 0

  return (
    <div className="rounded-xl border bg-card p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", ROLE_CLASS[role])}>
          {ROLE_LABELS[role]}
        </span>
        {!hasAccess && (
          <span className="text-[11px] text-muted-foreground">sin acceso web</span>
        )}
      </div>
      {hasAccess && (
        <div className="flex gap-4 mt-1">
          <div className="flex flex-col">
            <span className="text-2xl font-medium tabular-nums leading-none">{visible}</span>
            <span className="text-xs text-muted-foreground mt-0.5">secciones visibles</span>
          </div>
          <div className="flex flex-col">
            <span className="text-2xl font-medium tabular-nums leading-none">{writable}</span>
            <span className="text-xs text-muted-foreground mt-0.5">con escritura</span>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span className="flex items-center gap-1"><EyeIcon className="h-3 w-3" /> Ver</span>
      <span className="flex items-center gap-1"><PencilIcon className="h-3 w-3" /> Editar</span>
      <span className="flex items-center gap-1"><Trash2Icon className="h-3 w-3" /> Eliminar</span>
      <span className="flex items-center gap-1"><RotateCcwIcon className="h-3 w-3" /> Restaurar</span>
      <span className="flex items-center gap-1"><MinusIcon className="h-3 w-3" /> Sin acceso</span>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RolesPage() {
  return (
    <>
      <SiteHeader title="Roles y permisos" />
      <div className="flex flex-col gap-6 p-4 md:p-6">

        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {ROLES.map(r => <RoleCard key={r} role={r} />)}
        </div>

        <p className="text-sm text-muted-foreground -mt-2">
          Permisos definidos en{" "}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">
            packages/shared/src/config/permissions.ts
          </code>
          . Editar ese archivo y redesplegar para modificarlos.
        </p>

        {/* Matrix */}
        <div className="rounded-xl border overflow-hidden">
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-b bg-muted/30 flex-wrap">
            <h2 className="text-sm font-medium">Matriz de permisos</h2>
            <Legend />
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-52">Página</TableHead>
                  {ROLES.map(role => (
                    <TableHead key={role} className="text-center min-w-[160px]">
                      <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", ROLE_CLASS[role])}>
                        {ROLE_LABELS[role]}
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {NAV_GROUPS.map(({ group, items }) => (
                  <>
                    {/* Section header row */}
                    <TableRow key={group} className="bg-muted/40 hover:bg-muted/40">
                      <TableCell
                        colSpan={ROLES.length + 1}
                        className="py-1.5 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                      >
                        {group}
                      </TableCell>
                    </TableRow>

                    {/* Item rows */}
                    {items.map((item, i) => (
                      <TableRow key={`${group}-${i}`}>
                        <TableCell className="pl-6 text-sm text-foreground">
                          {item.label}
                        </TableCell>
                        {ROLES.map(role => (
                          <PermCell key={role} role={role} resource={item.resource} />
                        ))}
                      </TableRow>
                    ))}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

      </div>
    </>
  )
}
